import { useEffect, useRef, useState } from "react";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

const SR = typeof window !== "undefined"
  ? (window.SpeechRecognition || window.webkitSpeechRecognition)
  : null;

/**
 * Voice-to-text button for GlobalBuy AI.
 *
 * Why the previous attempts duplicated speech on mobile Chrome / Android:
 *   The handler walked the whole `event.results` array on every onresult event
 *   and re-committed finalized slots. Mobile Chrome re-emits finalized slots
 *   multiple times across events, so each pass re-appended the same text.
 *
 * The canonical Web-Speech-API pattern used here (`event.resultIndex`):
 *   - `continuous = true`, `interimResults = true` — one long cycle.
 *   - We ONLY process results starting at `event.resultIndex`, which is the
 *     index of the first result that is NEW in this event. This is defined by
 *     the spec and is the only mobile-safe way to iterate results. Every slot
 *     is therefore processed exactly once, even if the browser re-emits.
 *   - Final slots are appended to `finalBufferRef`; interim text is kept in a
 *     separate `interimRef` and emitted alongside the committed buffer.
 *   - On stop we call `.abort()` (hard cancel). `.stop()` can let the engine
 *     emit one more trailing final event that duplicates what the user already
 *     saw — abort prevents that entirely.
 *   - No auto-restart from onend. With `continuous=true` the engine stays open
 *     until the user taps Stop; onend only fires when the browser decides the
 *     session is over (timeout, no permission, OS-level kill).
 */
export function VoiceInputButton({ onTranscript, testId = "voice-input-btn", existingText = "" }) {
  const [listening, setListening] = useState(false);
  const [supported] = useState(() => !!SR);

  const recRef           = useRef(null);
  const baseRef          = useRef("");    // user's typed prefix captured at mic-start
  const finalBufferRef   = useRef("");    // speech committed as final so far
  const interimRef       = useRef("");    // live preview — reset each event
  const processedIndexRef = useRef(0);    // highest result index already consumed
  const wantListeningRef = useRef(false);
  const startingRef      = useRef(false);

  useEffect(() => () => { cleanupRecognition(); }, []);

  function cleanupRecognition() {
    const r = recRef.current;
    if (!r) return;
    try { r.onresult = null; r.onerror = null; r.onend = null; r.onstart = null; } catch {}
    try { r.abort(); } catch {}
    recRef.current = null;
  }

  function emit() {
    const combined = baseRef.current + finalBufferRef.current + interimRef.current;
    // Collapse accidental runs of spaces/tabs only; preserve newlines + single spaces.
    const cleaned = combined.replace(/[ \t]+/g, " ");
    onTranscript(cleaned);
  }

  function appendFinal(text) {
    const trimmed = text.trim();
    if (!trimmed) return;
    const buf = finalBufferRef.current;
    const needsSpace = buf.length > 0 && !/\s$/.test(buf);
    finalBufferRef.current = buf + (needsSpace ? " " : "") + trimmed + " ";
  }

  function createRecognition() {
    const r = new SR();
    r.continuous = true;
    r.interimResults = true;
    r.lang = navigator.language || "en-US";
    r.maxAlternatives = 1;

    r.onstart = () => {
      // Fresh session — nothing has been processed yet from this engine.
      processedIndexRef.current = 0;
      interimRef.current = "";
    };

    r.onresult = (e) => {
      // Spec: `event.resultIndex` is the index of the first *changed* result.
      // We iterate from max(resultIndex, processedIndex) so we never consume
      // the same slot twice even if the browser re-fires an event.
      const start = Math.max(e.resultIndex ?? 0, processedIndexRef.current);
      let interim = "";
      for (let i = start; i < e.results.length; i++) {
        const res = e.results[i];
        const t = res[0]?.transcript || "";
        if (res.isFinal) {
          appendFinal(t);
          processedIndexRef.current = i + 1;
        } else {
          interim += t;
        }
      }
      interimRef.current = interim;
      emit();
    };

    r.onerror = (ev) => {
      if (ev.error === "not-allowed" || ev.error === "service-not-allowed") {
        toast.error("Microphone permission denied.");
        wantListeningRef.current = false;
      } else if (ev.error === "audio-capture") {
        toast.error("No microphone detected.");
        wantListeningRef.current = false;
      } else if (ev.error === "no-speech" || ev.error === "aborted") {
        // Benign — silence timeout or deliberate user stop. Do not toast.
      } else {
        toast.error("Voice input error. Please try again.");
      }
    };

    r.onend = () => {
      // We intentionally do NOT auto-restart. If the engine ends while the
      // user still wants to listen, surface the state honestly instead of
      // creating phantom overlap sessions.
      interimRef.current = "";
      setListening(false);
      wantListeningRef.current = false;
    };

    return r;
  }

  const start = () => {
    if (!supported) {
      toast.error("Voice input isn't supported in this browser. Please type your request.");
      return;
    }
    if (listening || startingRef.current) return;
    startingRef.current = true;
    try {
      // Snapshot the user's typed text as a frozen prefix.
      const prefix = (existingText || "").replace(/\s+$/, "");
      baseRef.current = prefix ? prefix + " " : "";
      finalBufferRef.current = "";
      interimRef.current = "";
      processedIndexRef.current = 0;
      wantListeningRef.current = true;

      cleanupRecognition();
      const r = createRecognition();
      recRef.current = r;
      r.start();
      setListening(true);
    } catch {
      toast.error("Could not start voice input.");
      wantListeningRef.current = false;
      setListening(false);
    } finally {
      startingRef.current = false;
    }
  };

  const stop = () => {
    wantListeningRef.current = false;
    // Hard cancel — abort() prevents a trailing final event from duplicating text.
    try { recRef.current?.abort(); } catch {}
    interimRef.current = "";
    setListening(false);
    emit();
  };

  if (!supported) return null;

  return (
    <button
      type="button"
      onClick={listening ? stop : start}
      aria-label={listening ? "Stop voice input" : "Start voice input"}
      aria-pressed={listening}
      data-testid={testId}
      className={`inline-flex items-center justify-center h-10 w-10 rounded-md border transition-colors shrink-0 select-none ${
        listening
          ? "bg-red-500/10 border-red-500/40 text-red-500 animate-pulse"
          : "bg-transparent border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-white/10 dark:text-slate-300 dark:hover:bg-white/5"
      }`}
      title={listening ? "Listening… tap to stop" : "Speak your request"}
    >
      {listening ? <MicOff size={18} /> : <Mic size={18} />}
    </button>
  );
}
